<template>
    <form-modal
        ref="formModal"
        :title="i18nNewReport"
        size="xl"
        :in-editing="true"
        :handle-close="handleCancel">
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <confirmation-form-modal
                    ref="confirmationFormModal"
                    :title="i18nCheckinCheckoutPhotoReport"
                    :operation="'created'"
                />
                <b-tabs
                    id="checkin-checkout-tabs"
                    v-model="tabIndex"
                    class="tabs-pdv"
                    @activate-tab="tabActivated"
                >
                    <b-tab :title="i18nInformation">
                        <b-row>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nDate"
                                    label-for="filter-started-at-date-time"
                                    class="required label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_period')">
                                    <date-range-picker
                                        :child-class="validationClass($v.modal.start_date)"
                                        :value.sync="dateRange"
                                        :max-date="new Date()"
                                        :max-range-in-days="maxRangeInDays"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nExportType"
                                    label-for="export-type"
                                    class="required label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_type')"
                                >
                                    <translated-multiselect
                                        id="export-type"
                                        v-model="modal.type"
                                        :options="exportTypes"
                                        :disabled="submitLoading"
                                        :select-class="validationClass($v.modal.type)"
                                        @blur="$v.modal.type.$touch"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nExportOrder"
                                    label-for="export-order"
                                    class="required label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_option')"
                                >
                                    <translated-multiselect
                                        id="export-order"
                                        v-model="modal.order"
                                        :options="exportOrders"
                                        :disabled="submitLoading"
                                        :select-class="validationClass($v.modal.order)"
                                        @blur="$v.modal.order.$touch"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col md="6">
                                <b-form-group
                                    :label="i18nEmail"
                                    label-for="name-input"
                                    class="label-pdv"
                                >
                                    <b-form-input
                                        id="name-input"
                                        v-model.trim="modal.email"
                                        class="input-pdv-blue"
                                        type="email"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>

                    <b-tab :title="i18nFilters">
                        <b-row>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nClient"
                                    label-for="modal-client"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'modal-client'"
                                        ref="clientMultiselect"
                                        v-model="modal.clients"
                                        :service="'business-units'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nUser"
                                    label-for="export-user"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'export-user'"
                                        ref="exportUser"
                                        v-model="modal.users"
                                        :service="'users'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                        :parameters="usersParameters"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nNetwork"
                                    label-for="modal-network"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'modal-network'"
                                        ref="networkMultiselect"
                                        v-model="modal.networks"
                                        :service="'pos-chains'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nFlag"
                                    label-for="modal-point-of-sale-flag"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'modal-point-of-sale-flag'"
                                        ref="flagMultiselect"
                                        v-model="modal.flags"
                                        :service="'pos-flags'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nRegional"
                                    label-for="modal-regional"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'modal-regional'"
                                        ref="regionalMultiselect"
                                        v-model="modal.regionals"
                                        :service="'pos-regionals'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nPointOfSale"
                                    label-for="modal-point-of-sale"
                                    class="label-pdv"
                                >
                                    <multiSelectWithService
                                        :id="'modal-point-of-sale'"
                                        ref="pointOfSaleMultiselect"
                                        v-model="modal.point_of_sales"
                                        :service="'pos'"
                                        :searchable="true"
                                        :multiple="true"
                                        :disabled="submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>
                </b-tabs>
            </form>
        </template>
        <template v-slot:footer>
            <cancel-button
                ref="cancelButton"
                child-class="col-sm-4 float-left"
                :disabled="submitLoading"
                @onClick="handleCancel"
            />
            <save-button
                v-if="tabIndex !== 1"
                ref="okButton"
                child-class="col-sm-4 float-right ml-3"
                :title="getI18n('COMMON.next')"
                :busy="submitLoading"
                @onClick="tabIndex++"
            />
            <save-button
                v-else
                ref="okButton"
                child-class="col-sm-4 float-right ml-3"
                :busy="submitLoading"
                :title="i18nGenerateReport"
                @onClick="handleOk"
            />
        </template>
    </form-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import swalFeedback from '@src/mixins/swalFeedback.vue'
import validationMixin from '@src/mixins/validation.vue'
import fieldsValidation from '@src/mixins/fieldsValidation.vue'
import FormModal from '@/src/components/FormModal.vue'
import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
import translatedMultiselect from '@src/components/translatedMultiselect'
import multiSelectWithService from '@src/components/multiSelectWithService.vue'
import CancelButton from '@/src/components/CancelButton'
import SaveButton from '@/src/components/SaveButton'
import DateRangePicker from '@/src/components/DateRangePicker'
import filterVuetable from '@src/mixins/filterVuetable'
import CheckinCheckoutPhotoReportService from '@src/services/CheckinCheckoutPhotoReportService'
import checkinCheckoutPhotoReportTypes from '@src/mixins/checkinCheckoutPhotoReportTypes'
import moment from 'moment';

const i18nKey = 'CHECKIN_CHECKOUT_PHOTO_REPORTS'
const i18nCommon = 'COMMON'

export default {
    components: {
        FormModal,
        ConfirmationFormModal,
        DateRangePicker,
        translatedMultiselect,
        multiSelectWithService,
        CancelButton,
        SaveButton,
    },
    mixins: [
        validationMixin,
        swalFeedback,
        checkinCheckoutPhotoReportTypes,
        fieldsValidation,
        filterVuetable,
    ],
    data() {
        return {
            i18nKey: i18nKey,
            i18nCommon: i18nCommon,
            modal: {
                clients: [],
                users: [],
                networks: [],
                flags: [],
                regionals: [],
                point_of_sales: [],
                start_date: moment().startOf('day').toDate(),
                end_date: moment().startOf('day').toDate(),
                type: {
                    value: 'link_checkin',
                    text: this.getI18n(i18nKey, 'TYPES.link'),
                },
                order: {
                    value: 'point_of_sale',
                    text: this.getI18n(i18nKey, 'TITLES.pos_alias'),
                },
            },
            dateRange: {},
            maxRangeInDays: 15,
            submitLoading: false,
            tabIndex: 0,
            usersParameters: {
                per_page: 1000,
                page: 1,
                orderBy: 'name',
                sortedBy: 'asc',
                active: true,
            },
        }
    },
    validations: {
        modal: {
            start_date: {
                required,
                beforeEndDate(value, model) {
                    return value <= model.end_date
                },
                maxRangePeriod(value, model) {
                    return ((model.end_date - value) / 86400000) <= this.maxRangeInDays;
                },
            },
            end_date: {
                required,
                afterStartDate(value, model) {
                    return value >= model.start_date
                },
            },
            type: {
                required,
            },
            order: {
                required,
            },
        },
    },
    computed: {
        i18nDate() {
            return this.getI18n(i18nCommon, 'period')
        },
        i18nExportType() {
            return this.getI18n(i18nKey, 'TITLES.export_type')
        },
        i18nExportOrder() {
            return this.getI18n(i18nKey, 'TITLES.export_order')
        },
        i18nEmail() {
            return this.getI18n(i18nKey, 'TITLES.email')
        },
        i18nClient() {
            return this.getI18n(i18nKey, 'TITLES.client')
        },
        i18nUser() {
            return this.getI18n(i18nKey, 'TITLES.user')
        },
        i18nNetwork() {
            return this.getI18n(i18nKey, 'TITLES.network')
        },
        i18nFlag() {
            return this.getI18n('POS', 'TITLES.flag')
        },
        i18nRegional() {
            return this.getI18n(i18nKey, 'TITLES.regional')
        },
        i18nPointOfSale() {
            return this.getI18n(i18nKey, 'TITLES.point_of_sale')
        },
        i18nNewReport() {
            return this.getI18n(i18nKey, 'BUTTONS.new_report')
        },
        i18nGenerateReport() {
            return this.getI18n(i18nKey, 'BUTTONS.generate_report')
        },
        i18nCheckinCheckoutPhotoReport() {
            return this.getI18nModified({
                prefix: i18nKey,
                suffix: 'TITLES.checkin_checkout_photo_report',
                modifier: 2,
            })
        },
        i18nInformation() {
            return this.getI18nModified({
                prefix: 'COMMON.info',
                modifier: 2,
            })
        },
        i18nFilters() {
            return this.getI18nModified({
                prefix: i18nCommon,
                suffix: 'filter',
                modifier: 2,
            })
        },
    },
    watch: {
        dateRange: {
            deep: true,
            handler(newValue) {
                if (newValue) {
                    this.modal.start_date = newValue.start
                    this.modal.end_date = newValue.end

                    this.$v.modal.start_date.$touch()
                    this.$v.modal.end_date.$touch()
                }
                else {
                    this.modal.start_date = null
                    this.modal.end_date = null
                }
            },
        },
    },
    methods: {
        tabActivated(newTabIndex, oldTabIndex, event) {
            if (oldTabIndex === 0) {
                this.$v.modal.$touch()
                if (this.$v.modal.$invalid) {
                    event.preventDefault();
                }
            }
        },
        showModal() {
            this.submitLoading = false
            this.tabIndex = 0
            this.cleanModal()
            this.$refs.formModal.show()
        },
        cleanModal() {
            this.dateRange = {
                start: moment().startOf('day').toDate(),
                end: moment().startOf('day').toDate(),
            }

            this.modal = Object.assign(this.modal, {
                clients: [],
                users: [],
                networks: [],
                flags: [],
                regionals: [],
                point_of_sales: [],
                start_date: moment().startOf('day').toDate(),
                end_date: moment().startOf('day').toDate(),
                type: {
                    value: 'link_checkin',
                    text: this.getI18n(i18nKey, 'TYPES.link'),
                },
                order: {
                    value: 'point_of_sale',
                    text: this.getI18n(i18nKey, 'TITLES.pos_alias'),
                },
            })
        },
        resetValidation() {
            this.$nextTick(() => this.$v.$reset())
            this.clearAllResponseError()
        },
        handleCancel() {
            this.$refs.confirmationFormModal.showModal(true, this.handleClose)
        },
        handleClose(close) {
            if (!close) {
                return;
            }
            this.resetValidation()
            this.$refs.formModal.hide()
        },
        handleOk() {
            this.$v.modal.$touch()
            if (!this.$v.modal.$invalid && !this.submitLoading) {
                this.$refs.confirmationFormModal.showModal(false, this.handleSubmit)
            }
        },
        async handleSubmit(submit) {
            if (!submit) {
                return;
            }

            this.submitLoading = true

            const parameters = this.formatParameters({
                client_id: this.modal.clients.map((client) => client.id),
                user_id: this.modal.users.map((user) => user.id),
                network_id: this.modal.networks.map((network) => network.id),
                flag_id: this.modal.flags.map((flag) => flag.id),
                regional_id: this.modal.regionals.map((regional) => regional.id),
                point_of_sale_id: this.modal.point_of_sales.map((pointOfSale) => pointOfSale.id),
                filtered_start: moment(this.modal.start_date).format('YYYY-MM-DD'),
                filtered_end: moment(this.modal.end_date).format('YYYY-MM-DD'),
                type: this.modal.type.value,
                maximum_photos_per_slide: 1,
                order: this.modal?.order?.value ?? 'point_of_sale',
                language: 'pt_BR',
            })

            const response = await CheckinCheckoutPhotoReportService.create(parameters)
                .then((response) => response)
                .catch((error) => error)

            const statusCode = response.status.toString()

            if (statusCode.charAt(0) === '2') {
                this.$emit('refresh')
                this.resetValidation()
                this.$refs.formModal.hide()
                this.positiveFeedback(this.i18nCheckinCheckoutPhotoReport, 'created')
            }

            if (statusCode === '417') {
                this.warningFeedback(this.i18nCheckinCheckoutPhotoReport, response.data.message)
            }

            if (statusCode === '422') {
                this.modalResponseErrors = response.data.errors
            }

            this.submitLoading = false
        },
    },
}
</script>
